import NotificationImportantOutlined from '@mui/icons-material/NotificationImportantOutlined';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { isAppRenewChunkLossError } from '@vs/components/portal-sentry';
import { PortalButton } from '@vs/ui/portal/button';
import { PortalDialog } from '@vs/ui/portal/dialog';
import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

export interface PortalViewErrorBoundaryProps {
  enable?: boolean;
  children: ReactNode;
}

export function PortalViewErrorBoundary({
  enable = true,
  children,
}: PortalViewErrorBoundaryProps) {
  const { t } = useTranslation();

  return !enable ? (
    children
  ) : (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        const [title, desc, icon] = (() => {
          if (isAppRenewChunkLossError(error)) {
            return [
              t('shared:app_boundary.chunk_loss.title', {
                defaultValue: 'App has been updated',
              }),
              t('shared:app_boundary.chunk_loss.desc', {
                defaultValue:
                  'There is a new version of app, please reload to use the latest version app',
              }),
              <TipsAndUpdatesIcon className="text-[32px] me-2 text-vs-secondary-focus" />,
            ] as const;
          } else {
            return [
              t('shared:app_boundary.unexpected.title', {
                defaultValue: 'Something went wrong',
              }),
              t('shared:app_boundary.unexpected.desc', {
                defaultValue: 'App crash due to some unexpected issue.',
              }),
              <NotificationImportantOutlined className="text-[32px] me-2 text-vs-error-focus" />,
            ] as const;
          }
        })();
        return (
          <PortalDialog
            open
            size="sm"
            title={title}
            content={desc}
            titleIcon={icon}
            actions={
              <div className="text-right">
                <PortalButton
                  label={t('shared:common.btn.reload', {
                    defaultValue: 'Reload',
                  })}
                  onClick={() => window.location.reload()}
                  type="secondary"
                  variant="contained"
                />
              </div>
            }
          />
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default PortalViewErrorBoundary;
